import React, { useState, useMemo, useCallback } from 'react';
import createContextHook from '@nkzw/create-context-hook';
import { useAuth } from '@/context/AuthContext';
import { DogProfile } from '@/types/dog-profile';
import { useDogProfiles, useCreateDogProfile, useUpdateDogProfile } from '@/lib/queries/dogs';

export const [DogsContext, useDogs] = createContextHook(() => {
  const { user, session, isLoading: isAuthLoading } = useAuth();
  const token = session?.accessToken;

  // React Query hook for dog profiles
  const dogProfilesQuery = useDogProfiles(token);
  const createDogProfileMutation = useCreateDogProfile(token);
  const updateDogProfileMutation = useUpdateDogProfile(token);

  // Local state for active dog selection (not server-managed)
  const [activeDogId, setActiveDogId] = useState<string | null>(null);

  // Derive values from React Query
  const dogProfiles: DogProfile[] = dogProfilesQuery.data ?? [];

  // isDogProfileLoading should be true while auth is loading OR while the query is in
  // its initial fetch. When auth resolves with no user, the query stays disabled and
  // we consider loading done. When auth resolves with a user, the query starts fetching
  // and we wait for it.
  const isDogProfileLoading = isAuthLoading
    ? true  // auth hasn't resolved yet — we can't know if user has dogs
    : (!token ? false : dogProfilesQuery.isLoading && dogProfilesQuery.fetchStatus !== 'idle');
  // fetchStatus === 'idle' means the query is paused (enabled:false) or done fetching.

  const dogProfileError = dogProfilesQuery.isError;

  const dogProfile = useMemo(
    () => dogProfiles.find(d => d.id === activeDogId) || dogProfiles[0] || null,
    [dogProfiles, activeDogId]
  );

  // Set active dog when profiles first load
  useMemo(() => {
    if (dogProfiles.length > 0 && !activeDogId) {
      setActiveDogId(dogProfiles[0].id ?? null);
    }
  }, [dogProfiles]);

  const refreshDogProfile = useCallback(async () => {
    await dogProfilesQuery.refetch();
  }, [dogProfilesQuery]);

  const createDogProfile = useCallback(async (profile: Omit<DogProfile, 'id' | 'created_at' | 'updated_at'>) => {
    const newProfile = await createDogProfileMutation.mutateAsync(profile);
    // Auto-select the new dog
    setActiveDogId(newProfile.id ?? null);
    return newProfile;
  }, [createDogProfileMutation]);

  const updateDogProfile = useCallback(async (profile: DogProfile) => {
    return updateDogProfileMutation.mutateAsync(profile);
  }, [updateDogProfileMutation]);

  return useMemo(() => ({
    dogProfile,
    dogProfiles,
    activeDogId,
    setActiveDogId,
    isDogProfileLoading,
    hasDogProfile: dogProfiles.length > 0,
    dogProfileError,
    refreshDogProfile,
    createDogProfile,
    updateDogProfile,
  }), [dogProfile, dogProfiles, activeDogId, isDogProfileLoading, dogProfileError, refreshDogProfile, createDogProfile, updateDogProfile]);
});